export const GOV_USER_UPDATE_FIELDS = [
    'name', 'email', 'role', 'password', 'org_scope', 'vicepresidency_id',
    'commission_id', 'accessible_modules', 'accessible_instances', 'accessible_vps',
] as const;

const CANONICAL_MODULES = [
    'Dashboard', 'Talento', 'Estructura', 'Planeacion', 'Seguimiento', 'Reportes',
    'Memoria', 'Sincronizacion', 'Usuarios', 'Configuracion', 'OrgStructure',
    'Escritorio IA', 'Control Operativo',
] as const;
const token = (value: unknown): string => String(value || '').normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
const MODULE_BY_TOKEN = new Map(CANONICAL_MODULES.map((name) => [token(name), name]));
const SCOPES = new Set(['GLOBAL', 'VP', 'COORDINATOR', 'INSTANCE']);
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PASSWORD_PLACEHOLDER = /^[*\u2022\u25cf\u00b7]+$/u;

export class GovUserUpdateValidationError extends Error {}

const text = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined;
    return value.trim() || undefined;
};
const id = (value: unknown): string | undefined => text(value)?.toLowerCase();
const ids = (value: unknown): string[] => !Array.isArray(value) ? []
    : [...new Set(value.map(id).filter((entry): entry is string => !!entry))].sort();

export type GovUserUpdateNormalizationOptions = {
    accessibleInstanceCatalog?: Iterable<unknown>;
    accessibleVpCatalog?: Iterable<unknown>;
};

export type GovUserAccessibleInstancesDecision = {
    knownCurrent: string[];
    knownVpCurrent: string[];
    preservedLegacy: string[];
    removedLegacy: string[];
    newlyUnresolved: string[];
    catalogUnavailable: boolean;
};

const normalizeInstanceLabel = (value: unknown): string => String(value || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();

const LEGACY_ID_TO_CANONICAL_NAME: Record<string, string> = {
    'b0000000-0000-0000-0000-000000000001': 'educacion',
    'b0000000-0000-0000-0000-000000000003': 'salud',
};

const resolveInstanceCatalog = (accessibleInstanceCatalog?: Iterable<unknown>) => {
    const catalog = new Set<string>();
    const names = new Map<string, string>();
    const excluded = new Set<string>();
    const legacyUuidToCanonical = new Map<string, string>();

    for (const raw of accessibleInstanceCatalog || []) {
        const entry = typeof raw === 'object' && raw !== null
            ? raw as { id?: unknown; name?: unknown }
            : { id: raw };
        const instanceId = id(entry.id);
        if (!instanceId || !UUID_PATTERN.test(instanceId)) continue;
        catalog.add(instanceId);
        const label = normalizeInstanceLabel(entry.name);
        if (label === 'demo') {
            excluded.add(instanceId);
            continue;
        }
        if (label) names.set(label, instanceId);
    }

    // Resolver UUIDs históricos específicos hacia el UUID canónico actual en el catálogo
    for (const [legacyUuid, canonicalName] of Object.entries(LEGACY_ID_TO_CANONICAL_NAME)) {
        const currentCanonicalId = names.get(canonicalName);
        if (currentCanonicalId) {
            legacyUuidToCanonical.set(legacyUuid, currentCanonicalId);
        }
    }

    return { catalog, names, excluded, legacyUuidToCanonical };
};

const normalizeLegacyValue = (value: unknown): string => String(value || '').trim();
const legacyKey = (value: unknown): string => normalizeInstanceLabel(value);

export const classifyGovUserAccessibleInstances = (
    originalValue: unknown,
    draftValue: unknown,
    accessibleInstanceCatalog?: Iterable<unknown>,
    catalogAvailable = accessibleInstanceCatalog !== undefined,
    accessibleVpCatalog?: Iterable<unknown>,
): GovUserAccessibleInstancesDecision => {
    const { catalog, names, excluded, legacyUuidToCanonical } = resolveInstanceCatalog(accessibleInstanceCatalog);
    const vpCatalog = resolveInstanceCatalog(accessibleVpCatalog);
    const original = Array.isArray(originalValue) ? originalValue : [];
    const draft = Array.isArray(draftValue) ? draftValue : [];
    const originalKeys = new Set(original.map(legacyKey).filter(Boolean));
    const draftKeys = new Set(draft.map(legacyKey).filter(Boolean));
    const knownCurrent = new Set<string>();
    const knownVpCurrent = new Set<string>();
    const preservedLegacy = new Set<string>();
    const newlyUnresolved = new Set<string>();

    for (const raw of draft) {
        const candidate = id(raw);
        const label = normalizeInstanceLabel(raw);
        if (!candidate || label === 'demo' || excluded.has(candidate)) continue;
        const legacyCanonical = candidate ? legacyUuidToCanonical.get(candidate) : undefined;
        const resolved = legacyCanonical || (UUID_PATTERN.test(candidate)
            ? (catalog.has(candidate) ? candidate : undefined)
            : names.get(label));
        if (resolved) {
            knownCurrent.add(resolved);
        } else {
            const vpResolved = UUID_PATTERN.test(candidate)
                ? (vpCatalog.catalog.has(candidate) ? candidate : undefined)
                : vpCatalog.names.get(label);
            if (vpResolved) {
                knownVpCurrent.add(vpResolved);
            } else if (originalKeys.has(legacyKey(raw))) {
                preservedLegacy.add(normalizeLegacyValue(raw));
            } else {
                newlyUnresolved.add(normalizeLegacyValue(raw));
            }
        }
    }

    const removedLegacy = new Set<string>();
    for (const raw of original) {
        const candidate = id(raw);
        const label = normalizeInstanceLabel(raw);
        if (!candidate || label === 'demo' || excluded.has(candidate)) continue;
        const legacyCanonical = candidate ? legacyUuidToCanonical.get(candidate) : undefined;
        const resolved = legacyCanonical || (UUID_PATTERN.test(candidate)
            ? (catalog.has(candidate) ? candidate : undefined)
            : names.get(label));
        if (!resolved && !draftKeys.has(legacyKey(raw))) removedLegacy.add(normalizeLegacyValue(raw));
    }

    return {
        knownCurrent: [...knownCurrent].sort(),
        knownVpCurrent: [...knownVpCurrent].sort(),
        preservedLegacy: [...preservedLegacy].sort(),
        removedLegacy: [...removedLegacy].sort(),
        newlyUnresolved: [...newlyUnresolved].sort(),
        catalogUnavailable: !catalogAvailable,
    };
};

export const hasUnresolvableGovUserAccessibleInstances = (
    value: unknown,
    accessibleInstanceCatalog?: Iterable<unknown>,
): boolean => {
    const { catalog, names, excluded, legacyUuidToCanonical } = resolveInstanceCatalog(accessibleInstanceCatalog);
    if (!Array.isArray(value)) return false;
    return value.some((raw) => {
        const candidate = id(raw);
        if (!candidate) return false;
        if (excluded.has(candidate)) return false;
        if (legacyUuidToCanonical.has(candidate)) return false;
        if (UUID_PATTERN.test(candidate)) return !catalog.has(candidate);
        return normalizeInstanceLabel(raw) !== 'demo' && !names.has(normalizeInstanceLabel(raw));
    });
};

/** Resolves legacy instance names and keeps only IDs in the loaded catalog. */
export const normalizeGovUserAccessibleInstances = (
    value: unknown,
    accessibleInstanceCatalog?: Iterable<unknown>,
): string[] => {
    const { catalog, names, excluded, legacyUuidToCanonical } = resolveInstanceCatalog(accessibleInstanceCatalog);
    if (!Array.isArray(value)) return [];
    const resolved = value.map((raw) => {
        const candidate = id(raw);
        if (!candidate) return undefined;
        if (normalizeInstanceLabel(raw) === 'demo' || excluded.has(candidate)) return undefined;
        const legacyCanonical = candidate ? legacyUuidToCanonical.get(candidate) : undefined;
        if (legacyCanonical) return legacyCanonical;
        if (UUID_PATTERN.test(candidate)) {
            return catalog.has(candidate) ? candidate : undefined;
        }
        return names.get(normalizeInstanceLabel(raw));
    }).filter((entry): entry is string => !!entry);
    return [...new Set(resolved)].sort();
};

/** Resolves explicit VP selections and legacy VP values misplaced in instance scope. */
export const normalizeGovUserAccessibleVps = (
    value: unknown,
    accessibleVpCatalog?: Iterable<unknown>,
    legacyInstanceValues: unknown = [],
): string[] => {
    const { catalog, names } = resolveInstanceCatalog(accessibleVpCatalog);
    if (!Array.isArray(value) && !Array.isArray(legacyInstanceValues)) return [];
    const resolved = [...(Array.isArray(value) ? value : []), ...(Array.isArray(legacyInstanceValues) ? legacyInstanceValues : [])]
        .map((raw) => {
            const candidate = id(raw);
            if (!candidate) return undefined;
            return UUID_PATTERN.test(candidate)
                ? (catalog.has(candidate) ? candidate : undefined)
                : names.get(normalizeInstanceLabel(raw));
        })
        .filter((entry): entry is string => !!entry);
    return [...new Set(resolved)].sort();
};

export const canonicalizeGovUserModules = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    const modules = new Map<string, string>();
    for (const raw of value) {
        if (typeof raw !== 'string') continue;
        const [rawName, rawPermission = 'EDITOR'] = raw.split(':', 2);
        const moduleToken = token(rawName);
        const moduleName = MODULE_BY_TOKEN.get(moduleToken);
        const permission = String(rawPermission || '').trim().toUpperCase();
        if (!moduleName || permission === 'OFF') continue;
        if (permission === 'EDITOR' || permission === 'VISOR') {
            modules.set(moduleToken, `${moduleName}:${permission}`);
        }
    }
    return [...modules.values()].sort();
};

type CanonicalUserUpdate = {
    name?: string;
    email?: string;
    role?: string;
    org_scope?: string;
    vicepresidency_id?: string;
    commission_id?: string;
    accessible_modules: string[];
    accessible_instances: string[];
    accessible_vps: string[];
};

export const canonicalizeGovUserUpdateSnapshot = (
    value: Record<string, unknown>,
    options: GovUserUpdateNormalizationOptions = {},
): CanonicalUserUpdate => ({
    name: text(value.name),
    email: text(value.email)?.toLowerCase(),
    role: text(value.role)?.toUpperCase(),
    org_scope: text(value.org_scope)?.toUpperCase(),
    vicepresidency_id: id(value.vicepresidency_id),
    commission_id: id(value.commission_id),
    accessible_modules: canonicalizeGovUserModules(value.accessible_modules),
    accessible_instances: options.accessibleInstanceCatalog === undefined
        ? ids(value.accessible_instances)
        : normalizeGovUserAccessibleInstances(
            value.accessible_instances,
            options.accessibleInstanceCatalog,
        ),
    accessible_vps: options.accessibleVpCatalog === undefined
        ? ids(value.accessible_vps)
        : normalizeGovUserAccessibleVps(
            value.accessible_vps,
            options.accessibleVpCatalog,
            value.accessible_instances,
        ),
});

const equal = (left: string[], right: string[]): boolean =>
    left.length === right.length && left.every((entry, index) => entry === right[index]);
const validId = (value: string | undefined, field: string): string => {
    if (!value || !UUID_PATTERN.test(value)) {
        throw new GovUserUpdateValidationError(`El alcance requiere un valor valido para ${field}.`);
    }
    return value;
};
const validIds = (value: string[], field: string): string[] => {
    if (value.some((entry) => !UUID_PATTERN.test(entry))) {
        throw new GovUserUpdateValidationError(`El alcance contiene valores invalidos en ${field}.`);
    }
    return value;
};

const addChangedScope = (changes: Record<string, unknown>, current: CanonicalUserUpdate): void => {
    const scope = current.org_scope;
    if (!scope || !SCOPES.has(scope)) throw new GovUserUpdateValidationError('El alcance seleccionado no es valido.');
    changes.org_scope = scope;
    if (scope === 'VP') changes.vicepresidency_id = validId(current.vicepresidency_id, 'vicepresidency_id');
    if (scope === 'INSTANCE') changes.commission_id = validId(current.commission_id, 'commission_id');
    if (scope === 'COORDINATOR') {
        const instances = validIds(current.accessible_instances, 'accessible_instances');
        const vps = validIds(current.accessible_vps, 'accessible_vps');
        if (instances.length === 0 && vps.length === 0) {
            throw new GovUserUpdateValidationError('El alcance coordinador requiere al menos una instancia o vicepresidencia.');
        }
        changes.accessible_instances = instances;
        changes.accessible_vps = vps;
    }
};

/** Builds a canonical, allowlisted and strictly differential PATCH. */
export const buildGovUserUpdateChanges = (
    currentValue: Record<string, unknown>, originalValue: Record<string, unknown>,
    options: GovUserUpdateNormalizationOptions = {},
    instancesScopeTouched = true,
    vpsScopeTouched = true,
): Record<string, unknown> => {
    const current = canonicalizeGovUserUpdateSnapshot(currentValue, options);
    const original = canonicalizeGovUserUpdateSnapshot(originalValue);
    const changes: Record<string, unknown> = {};
    for (const field of ['name', 'email', 'role'] as const) {
        if (current[field] !== original[field] && current[field] !== undefined) changes[field] = current[field];
    }
    const password = typeof currentValue.password === 'string' ? currentValue.password : '';
    const confirmation = typeof currentValue.confirmPassword === 'string' ? currentValue.confirmPassword : '';
    if (password.trim() && !PASSWORD_PLACEHOLDER.test(password.trim())) {
        if (password !== confirmation) throw new GovUserUpdateValidationError('Las contrasenas no coinciden.');
        changes.password = password;
    }
    if (!equal(current.accessible_modules, original.accessible_modules)) {
        changes.accessible_modules = current.accessible_modules;
    }
    if (current.org_scope !== original.org_scope) {
        addChangedScope(changes, current);
        return changes;
    }
    if (current.org_scope === 'COORDINATOR') {
        const instancesChanged = instancesScopeTouched
            && !equal(current.accessible_instances, original.accessible_instances);
        const vpsChanged = vpsScopeTouched
            && !equal(current.accessible_vps, original.accessible_vps);
        if (instancesChanged || vpsChanged) {
            validIds(current.accessible_instances, 'accessible_instances');
            validIds(current.accessible_vps, 'accessible_vps');
            if (!current.accessible_instances.length && !current.accessible_vps.length) {
                throw new GovUserUpdateValidationError('El alcance coordinador requiere al menos una instancia o vicepresidencia.');
            }
            if (instancesChanged) changes.accessible_instances = current.accessible_instances;
            if (vpsChanged) changes.accessible_vps = current.accessible_vps;
        }
    } else if (current.org_scope === 'VP' && current.vicepresidency_id !== original.vicepresidency_id) {
        changes.vicepresidency_id = validId(current.vicepresidency_id, 'vicepresidency_id');
    } else if (current.org_scope === 'INSTANCE' && current.commission_id !== original.commission_id) {
        changes.commission_id = validId(current.commission_id, 'commission_id');
    }
    return changes;
};
